/* eslint-disable angular/timeout-service */

import { nodeChangeListenerServiceModule } from '../node-change-listener/node-change-listener';
import {
  WINDOW_EVT_RESIZE,
  windowEventHelperModule,
} from '../../helpers/window-event-helper/window-event-helper';
import { CustomEvent } from '../../utils/event-utils';
import { UI_LAYOUT_EVT_DRAW_COMPLETE } from '../../components/ui-layout/ui-layout';

export const scrollMonitorServiceModule = angular
  .module('Q.services.scroll-monitor-service', [
    nodeChangeListenerServiceModule,
    windowEventHelperModule,
  ])

  /**
   * A service to make it easier to monitor scrolling and perform updates based on it.
   *
   * The service reduces event listeners by grouping scroll listeners per item, and also sharing a single
   * window.resize listener across all scroll listeners.
   *
   * The individual node scroll listeners can further benefit by including an optional element with the event
   * listener.  In this case, the single onScroll event will also update whether or not the item is visible,
   * and set it on the scroll listener return object.
   *
   */
  .factory(
    'scrollMonitorService',
    function (
      $document,
      $window,
      nodeChangeListener,
      $rootScope,
      $timeout,
      $log,
    ) {
      const scrollListeners = [];
      const doc = $document[0];

      /**
       * Creates a Listener for scroll-related events on a given viewport (DOM node or $window)
       */
      class Listener {
        /**
         * Create a new Listener object
         * @param viewport {string|HTMLElement} The viewport on which we want to listen, defaults to window
         */
        constructor(viewport) {
          this.viewport = viewport;
          this.$viewport = angular.element(viewport);
          this.scrollWatchers = [];
          this.lastScrollTop = 0;
          this.lastHeight = 0;
          this.$destroyed = false;

          this.scrollTrigger = this.triggerUpdate.bind(this);
          this.$viewport.on('scroll touchmove', this.scrollTrigger);
          // ensure we always clean up if the dom node is destroyed
          this.$viewport.on('$destroy', () => this.destroy());

          // for non-window-level watches, we also look for DOM changes
          // otherwise, the resize event should be good enough
          if (viewport.nodeType) {
            this.nodeChangeListenerStop = nodeChangeListener.listen(
              viewport,
              (event) => {
                this.triggerUpdate(event, true);
              },
            );
          }

          scrollListeners.push(this);
        }

        /**
         * Trigger an update to all registered scrollWatchers
         * The goal with this design was to minimize calculation of the same data as much as possible.
         * If we have hundreds of event listeners, we don't want to perform the same work looking them up
         * every time.
         *
         * @param {Event} event The Event object for this update
         * @param {boolean} forceUpdate If true, forces an update regardless of scroll or height changes
         */
        triggerUpdate(event, forceUpdate) {
          if (this.$destroyed) return;

          const viewport = this.$viewport;
          const viewportScrollTop = viewport.scrollTop();
          const viewportHeight = viewport.offsetHeight();

          if (
            forceUpdate !== true &&
            viewportScrollTop === this.lastScrollTop &&
            viewportHeight === this.lastHeight
          )
            return;

          this.lastScrollTop = viewportScrollTop;
          this.lastHeight = viewportHeight;

          const viewportTop = this.$viewport.offsetTop();

          const len = this.scrollWatchers.length;
          let i = len - 1;
          let sw;
          let elementTop;
          let elementHeight;
          let data;

          for (; i >= 0; i--) {
            if (viewport.length === 0) {
              $log.error(
                "Invariant violation: expected viewport[0] to be defined but it wasn't",
              );
              continue;
            }

            data = {
              viewport,
              viewportTop,
              viewportHeight,
              viewportScrollTop,
              viewportScrollHeight: viewport[0].scrollHeight,
            };
            sw = this.scrollWatchers[i];
            if (sw) {
              if (sw.$elem) {
                // note whether the element is visible
                elementTop = sw.$elem.offsetTop();
                elementHeight = sw.elem.offsetHeight;
                data.element = sw.elem;
                data.elementTop = elementTop;
                data.elementHeight = elementHeight;
                Listener._setIsVisible(
                  viewportTop,
                  viewportHeight,
                  viewportScrollTop,
                  sw,
                  data,
                );
              }
              sw.notify(event, data);
            }
          }
        }

        /**
         * Mainly used to set the initial isVisible value on a ScrollWatcher, but it can also be used to
         * update the isVisible value out-of-band
         *
         * @param scrollWatcher
         */
        setIsVisible(scrollWatcher) {
          Listener._setIsVisible(
            this.$viewport.offsetTop(),
            this.$viewport.offsetHeight(),
            this.$viewport.scrollTop(),
            scrollWatcher,
            {},
          );
        }

        static _setIsVisible(
          viewportTop,
          viewportHeight,
          viewportScrollTop,
          scrollWatcher,
          data,
        ) {
          const elementTop = scrollWatcher.$elem.offsetTop() - viewportTop;
          const elementHeight = scrollWatcher.elem.offsetHeight;
          data.element = scrollWatcher.elem;
          data.elementTop = elementTop;
          data.elementHeight = elementHeight;
          scrollWatcher.isVisible = data.isVisible =
            // ensure the top is above the bottom of the viewport (with optional offset)
            viewportScrollTop + viewportHeight + scrollWatcher.offset >
              elementTop &&
            // ensure the bottoms is below the top of the viewport (with optional offset)
            viewportScrollTop - scrollWatcher.offset <
              elementTop + elementHeight;
        }

        addScrollWatcher(scrollWatcher) {
          if (this.$destroyed) return;
          this.scrollWatchers.push(scrollWatcher);
          $timeout.cancel(this._swAddedEvent);
          this._swAddedEvent = $timeout(() => {
            this.triggerUpdate(new CustomEvent('scrollWatcherAdded'), true);
          });
        }

        removeScrollWatcher(scrollWatcher) {
          let idx = this.scrollWatchers.indexOf(scrollWatcher);
          if (idx > -1) {
            this.scrollWatchers.splice(idx, 1);
            if (this.scrollWatchers.length === 0) {
              this.destroy();
            }
          }
        }

        destroy() {
          this.$viewport.off('scroll touchmove', this.scrollTrigger);
          if (this.nodeChangeListenerStop) {
            this.nodeChangeListenerStop();
          }
          this.scrollWatchers.length = 0;
          let idx = scrollListeners.indexOf(this);
          if (idx > -1) {
            scrollListeners.splice(idx, 1);
          }
          this.$destroyed = true;
        }
      }

      class ScrollWatcher {
        constructor(listener, callback, elem, offset = 0) {
          this.listener = listener;
          this.callback = callback;
          if (elem) {
            elem = _getElement(elem);
            this.elem = elem;
            this.$elem = angular.element(elem);
            this.offset = offset;
            this.update();
          }
        }

        update() {
          this.listener.setIsVisible(this);
        }

        notify(event, data) {
          this.callback(event, data, this);
        }

        destroy() {
          this.listener.removeScrollWatcher(this);
        }
      }

      const svc = {
        /**
         * Adds a scroll listener to the provided viewport.
         *
         * @param viewport {HTMLElement|String} The node or selector for the node to watch
         * @param callback {function} The callback function on scroll (will be called with event, viewportData, and ScrollWatcher arguments
         * @param elem {HTMLElement|String} Optional element to monitor for visibility and offset information
         * @param offset Optional value for adding an offset when calculating whether the elem is visible or not
         * @returns {ScrollWatcher}
         */
        addListener(viewport, callback, elem = null, offset = 0) {
          viewport = _getElement(viewport);
          let listener = scrollListeners.find((e) => e.viewport === viewport);
          if (!listener) {
            listener = new Listener(viewport);
          }

          let scrollWatcher = new ScrollWatcher(
            listener,
            callback,
            elem,
            offset,
          );

          listener.addScrollWatcher(scrollWatcher);
          return scrollWatcher;
        },

        updateListeners(event) {
          let i = 0;
          let len = scrollListeners.length;
          for (; i < len; i++) {
            if (!scrollListeners[i]) {
              $log.error(
                "Invariant violation: scrollListeners[i] was supposed to be defined but wasn't",
              );
              continue;
            }

            scrollListeners[i].triggerUpdate(event, true);
          }
        },
      };

      // on resize, fire all scroll listeners, but use only one listener for the whole application
      $rootScope.$on(WINDOW_EVT_RESIZE, ($e, event) =>
        svc.updateListeners(event),
      );

      // also capture ui layout events
      $rootScope.$on(UI_LAYOUT_EVT_DRAW_COMPLETE, svc.updateListeners);

      return svc;

      //// private methods

      // returns a pure DOM element, or window if none provided
      function _getElement(elem) {
        if (!elem) return $window;

        if (angular.isString(elem)) {
          elem = doc.querySelector(elem);
        } else if (elem.length) {
          elem = elem[0];
        }

        if (!elem) {
          throw new Error(
            `Unable to find element for scrollMonitorService: ${elem}`,
          );
        }

        if (elem.nodeType === 9) {
          elem = $window;
        }

        return elem;
      }
    },
  );
